import { MeasureConfigAverageType } from "@algomusinc/domain";

const BuildMivHierarchy = (flatList: any[], fbForecastMIVResponseModel: string): any[] => {
    const nodeMap = new Map<number, any>(); // Fast lookup for nodes
    const rootNodes: any[] = []; // Store root elements

    // Add nodes to map
    flatList.forEach(node => nodeMap.set(node.data, { ...node, children: [] }));

    // Assign children to parents dynamically
    flatList.forEach(node => {
      if (node.parentId) {
        const parent = nodeMap.get(node.parentId);
        if (parent) {
          parent.children.push(nodeMap.get(node.data));
        }
      } else {
        rootNodes.push(nodeMap.get(node.data)); // Root nodes (highest level)
      }
    });

    return rootNodes;
  }
  function AggregateMivMeasures(
    node: any,
    parentKey: string,
    fbForecastMIVResponseModelKey: string,
    masterMeasureList: Array<any>
  ) {
    // Base case: If the node is a leaf node, return
    if (!node.children || node.children.length === 0) {
      return node;
    }

    // Initialize parent node's forecast model key with an empty array
    node[fbForecastMIVResponseModelKey] = [];

    let avgCount = 0;

    // Aggregate from children
    node.children.forEach((child: any) => {
      AggregateMivMeasures(child, parentKey, fbForecastMIVResponseModelKey, masterMeasureList);

      if (!child[fbForecastMIVResponseModelKey]) return;

      // Copy structure from first valid child node
      if (node[fbForecastMIVResponseModelKey].length === 0) {
        node[fbForecastMIVResponseModelKey] = JSON.parse(JSON.stringify(child[fbForecastMIVResponseModelKey]));
        node[fbForecastMIVResponseModelKey].forEach((item: any) => {
          // preparing measures object e.g., `measures{AlgoBaseLineFcst: 0, ...}`
          masterMeasureList.forEach(measure => {
            const config = measure.measureConfigData;
            if (config.isComment) {
              item.measures[measure.code] = ""; // Text measures
            }
            else {
              item.measures[measure.code] = 0; // Numeric measures
            }
          });
        });
      }

      // Aggregate measures from child nodes
      node[fbForecastMIVResponseModelKey].forEach((parentItem: any, index: number) => {
        const childItem = child[fbForecastMIVResponseModelKey]?.[index];

        if (childItem) {
          masterMeasureList.forEach(measure => {
            const config = measure.measureConfigData;
            if (config.isComment) {
              parentItem[parentKey][measure.code] = childItem[parentKey][measure.code]; // Copy last comment value
            } else if (config.isLowHierarchy || config.isMultipleAggregated) {
              parentItem[parentKey][measure.code] = childItem[parentKey][measure.code];
            } else if (config.isSpreadEqual) {
              if (config.isPercent) {
                node[parentKey][measure.code] = child[parentKey][measure.code];
              } else {
                parentItem[parentKey][measure.code] = Math.max(parentItem[parentKey][measure.code], childItem[parentKey][measure.code]);
              }
            } else if (config.isAveraged === MeasureConfigAverageType.RollupAverage
                || config.isAveraged === MeasureConfigAverageType.BothTotalAndRollupAverage
            ) {
                if (!parentItem[parentKey].__avgTemp) parentItem[parentKey].__avgTemp = {};
                if (!parentItem[parentKey].__avgTemp[measure.code]) {
                  parentItem[parentKey].__avgTemp[measure.code] = { sum: 0, count: 0 };
                }

                const value = parseFloat(childItem[parentKey][measure.code]);
                if (!isNaN(value)) {
                  if((config.ignoreZeros && value > 0 ) || !config.ignoreZeros){
                    parentItem[parentKey].__avgTemp[measure.code].sum += value;
                    parentItem[parentKey].__avgTemp[measure.code].count += 1;
                  }
                }
            } else if (config.isPercent) {
              parentItem[parentKey][measure.code] = childItem[parentKey][measure.code]
            } else {
              parentItem[parentKey][measure.code] += parseFloat(childItem[parentKey][measure.code] || "0"); // Sum numerical values
            }
          });
        }
      });

      avgCount++;
    });

    // Roll-up average
    node[fbForecastMIVResponseModelKey].forEach((parentItem: any, index: number) => {
      masterMeasureList.forEach(measure => {
        const config = measure.measureConfigData;
        if (config.isAveraged === MeasureConfigAverageType.RollupAverage
            || config.isAveraged === MeasureConfigAverageType.BothTotalAndRollupAverage
        ) {
          if (parentItem[parentKey].__avgTemp && parentItem[parentKey].__avgTemp[measure.code]) {
            const avgData = parentItem[parentKey].__avgTemp[measure.code];
            if (avgData.count > 0) {
              parentItem[parentKey][measure.code] = avgData.sum / avgData.count; // Calculate average
            } else {
              parentItem[parentKey][measure.code] = 0; // Default to 0 if no valid data
            }
          }
        }
      });
      if (parentItem[parentKey].__avgTemp) {
        delete parentItem[parentKey].__avgTemp;
      }
    });

    // Handle averaging for transformed measures

    node[fbForecastMIVResponseModelKey].forEach((parentItem: any, index: number) => {
      const childItem = node[fbForecastMIVResponseModelKey][index];

      if (childItem) {
        masterMeasureList.forEach(measure => {
          const config = measure.measureConfigData;
          if (config.isMultipleAggregated || config.isMultiple) {
            node[fbForecastMIVResponseModelKey][index].measures[measure.code] = node.children.every((c: any) => c[fbForecastMIVResponseModelKey][index].measures[measure.code] == node[fbForecastMIVResponseModelKey][index].measures[measure.code]) ? node[fbForecastMIVResponseModelKey][index].measures[measure.code]: "Multiple";
          }
          if (config.isComment) {
            node[fbForecastMIVResponseModelKey][index].measures[measure.code] = node.children.every((c: any) => c[fbForecastMIVResponseModelKey][index].measures[measure.code] == node[fbForecastMIVResponseModelKey][index].measures[measure.code]) ? node[fbForecastMIVResponseModelKey][index].measures[measure.code]: "Multiple Comments";
          }
          if (config.isPercent) {
            node[fbForecastMIVResponseModelKey][index].measures[measure.code] = node.children.every((c: any) => c[fbForecastMIVResponseModelKey][index].measures[measure.code] == node[fbForecastMIVResponseModelKey][index].measures[measure.code]) ? node[fbForecastMIVResponseModelKey][index].measures[measure.code]: "Multiple %";
          }
        });
      }
    });
    return node;
  }


  export const ProcessMivResponse = (flatList: any[], parentKey: string, masterMeasureList: Array<any>) => {
    const tree = BuildMivHierarchy(flatList, 'fbForecastMIVResponseModel');
    tree.forEach(rootNode =>
      AggregateMivMeasures(rootNode, parentKey, 'fbForecastMIVResponseModel', masterMeasureList)
    );
    tree.forEach(rootNode =>
      AggregateMivMeasures(rootNode, parentKey, 'fbForecastMIVResponseModelDC', masterMeasureList)
    );
    return tree;
  }
